import React from 'react';
import { Download, FileText, Table2, Info } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { PageHeader } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';
import ContactStrip from '../components/ContactStrip';
import SEO from '../components/SEO';
import { getSeo, SEO_KEYWORDS } from '../data/seo';
import { GRADES, PROPERTIES, DL_TEXT } from '../data/downloads';
import { CONTACT, SITE_URL } from '../data/site';
import { track } from '../lib/analytics';

const download = (filename, content, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const Downloads = () => {
  const { t, lang } = useLang();
  const seo = getSeo('downloads', lang);
  const tx = DL_TEXT[lang];

  const buildSheet = (g) => {
    const gl = g[lang];
    const lines = [];
    lines.push('====================================================');
    lines.push(`  metakaolin.info  –  ${tx.subtitle.toUpperCase()}`);
    lines.push('====================================================');
    lines.push('');
    lines.push(`${tx.grade}: ${g.name}  (${gl.tag})`);
    lines.push('');
    lines.push(gl.desc);
    lines.push('');
    lines.push('----------------------------------------------------');
    PROPERTIES.forEach((p) => {
      const label = `${p[lang]} (${p.unit})`;
      lines.push(`${label.padEnd(38, ' ')} : ${g.values[p.key]}`);
    });
    lines.push(`${tx.appearance.padEnd(38, ' ')} : ${gl.appearance}`);
    lines.push(`${tx.dosage.padEnd(38, ' ')} : ${gl.dosage}`);
    lines.push('----------------------------------------------------');
    lines.push('');
    lines.push(tx.note);
    lines.push('');
    lines.push(`${CONTACT.company} · ${CONTACT.street}, ${CONTACT.city}, ${CONTACT.country}`);
    lines.push(`${CONTACT.email} · ${CONTACT.phone} · ${SITE_URL}`);
    return lines.join('\n');
  };

  const buildCsv = (g) => {
    const rows = [['property', 'unit', 'value']];
    PROPERTIES.forEach((p) => rows.push([p.en, p.unit, String(g.values[p.key])]));
    rows.push(['appearance', '', g[lang].appearance]);
    rows.push(['recommended dosage', '', g[lang].dosage]);
    return rows.map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
  };

  return (
    <div>
      <SEO title={seo.title} description={seo.description} path="/downloads" lang={lang} keywords={SEO_KEYWORDS[lang]} />
      <PageHeader label={t('nav.downloads')} title={seo.title} lead={seo.description} imageKey="powderBeige" />

      <section className="bg-white py-16 lg:py-24">
        <div className="container-wide">
          {/* Grade cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
            {GRADES.map((g, i) => {
              const gl = g[lang];
              return (
                <Reveal key={g.id} delay={(i % 2) * 90}>
                  <article className="h-full flex flex-col rounded-xl border border-slate-200 bg-white p-6 lg:p-7 hover:border-blue-300 hover:shadow-md hover:shadow-blue-700/5 transition">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{gl.tag}</span>
                        <h3 className="mt-1.5 text-xl font-extrabold text-slate-900 tracking-tight">{g.name}</h3>
                      </div>
                      <div className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-blue-700/5 border border-blue-700/15 text-blue-700 shrink-0">
                        <FileText className="w-5 h-5" />
                      </div>
                    </div>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{gl.desc}</p>

                    <dl className="mt-5 grid grid-cols-2 gap-x-5 gap-y-2.5 border-t border-slate-100 pt-5">
                      {PROPERTIES.map((p) => (
                        <div key={p.key} className="flex items-baseline justify-between gap-2">
                          <dt className="text-[12.5px] text-slate-500">{p[lang]}</dt>
                          <dd className="text-[13px] font-bold text-slate-900 whitespace-nowrap">{g.values[p.key]} <span className="font-medium text-slate-400">{p.unit}</span></dd>
                        </div>
                      ))}
                    </dl>
                    <div className="mt-4 grid grid-cols-2 gap-3 text-[12.5px]">
                      <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                        <p className="text-slate-400 font-semibold uppercase tracking-wide text-[10px]">{tx.dosage}</p>
                        <p className="mt-0.5 font-semibold text-slate-800">{gl.dosage}</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                        <p className="text-slate-400 font-semibold uppercase tracking-wide text-[10px]">{tx.appearance}</p>
                        <p className="mt-0.5 font-semibold text-slate-800">{gl.appearance}</p>
                      </div>
                    </div>

                    <div className="mt-6 flex flex-wrap gap-3">
                      <button
                        onClick={() => { track('download_datasheet', { grade: g.id }); download(`${g.id}-datasheet.txt`, buildSheet(g), 'text/plain;charset=utf-8'); }}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-blue-700 text-white text-[13px] font-semibold hover:bg-blue-800 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        {tx.downloadSheet}
                      </button>
                      <button
                        onClick={() => { track('download_csv', { grade: g.id }); download(`${g.id}-values.csv`, buildCsv(g), 'text/csv;charset=utf-8'); }}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white border border-slate-300 text-slate-800 text-[13px] font-semibold hover:border-blue-400 hover:text-blue-700 transition-colors"
                      >
                        <Table2 className="w-4 h-4" />
                        {tx.downloadCsv}
                      </button>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>

          {/* Comparison table */}
          <div className="mt-14">
            <h2 className="text-[22px] font-extrabold tracking-tight text-slate-900">{tx.tableTitle}</h2>
            <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-left border-collapse min-w-[640px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-3 text-[12px] font-bold uppercase tracking-wide text-slate-500">{tx.property}</th>
                    {GRADES.map((g) => (
                      <th key={g.id} className="px-4 py-3 text-[13px] font-bold text-slate-900">{g.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PROPERTIES.map((p, r) => (
                    <tr key={p.key} className={r % 2 ? 'bg-slate-50/50' : 'bg-white'}>
                      <td className="px-4 py-3 text-[13px] font-semibold text-slate-700">{p[lang]} <span className="text-slate-400 font-medium">({p.unit})</span></td>
                      {GRADES.map((g) => (
                        <td key={g.id} className="px-4 py-3 text-[13px] text-slate-800 font-medium">{g.values[p.key]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-8 flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 px-5 py-4">
            <Info className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
            <p className="text-[13.5px] text-slate-700 leading-relaxed">{tx.note}</p>
          </div>
        </div>
      </section>
      <ContactStrip />
    </div>
  );
};

export default Downloads;
